import { createColumnHelper } from "@tanstack/react-table";
import { formatDate } from "date-fns";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Button } from "semantic-ui-react";

import { CopyToClipboard, DATE_FORMATS_FNS, ReactTable, usePermanentLocations, usePermanentRealtimeDealers } from "components";
import { FailedInvoicesModal, InvoiceHistoryModal } from "modules/ExactMonitoring/components/Billing/components";
import "modules/ExactMonitoring/components/Billing/components/BillingTable/BillingTable.scss";

import type { ExactFailedInvoice } from "models";
import type { DealersAndLocationsWithFailedInvoices } from "modules/ExactMonitoring/components/Billing/hooks";

type FailedInvoiceModalPayload = {
  exactFailedInvoices: ExactFailedInvoice[];
  dealerId?: number;
  dealerLocationId?: number;
};

type SelectedDealerOrLocationId = {
  dealerId?: number;
  dealerLocationId?: number;
};

type BillingTableProps = {
  isLoading: boolean;
  invoicedDealersAndLocations?: DealersAndLocationsWithFailedInvoices[];
};

const columnHelper = createColumnHelper<DealersAndLocationsWithFailedInvoices>();

export const BillingTable = ({ isLoading, invoicedDealersAndLocations = [] }: BillingTableProps) => {
  const { t } = useTranslation();

  const [selectedDealerIdForInvoices, setSelectedDealerIdForInvoices] = useState(0);
  const [selectedLocationIdForInvoices, setSelectedLocationIdForInvoices] = useState(0);
  const [selectedDealerOrLocationId, setSelectedDealerOrLocationId] = useState<SelectedDealerOrLocationId | null>(null);
  const { locations } = usePermanentLocations();
  const { dealers } = usePermanentRealtimeDealers();

  const handleCloseFailedInvoiceModal = () => setSelectedDealerOrLocationId(null);
  const handleOpenFailedInvoiceModal = (payload: SelectedDealerOrLocationId) => setSelectedDealerOrLocationId(payload);

  const handleCloseInvoiceHistoryModal = () => {
    setSelectedDealerIdForInvoices(0);
    setSelectedLocationIdForInvoices(0);
  };

  const handleOpenShowInvoiceHistoryModal = (dealerId?: number, dealerLocationId?: number) => {
    setSelectedDealerIdForInvoices(dealerId || 0);
    setSelectedLocationIdForInvoices(dealerLocationId || 0);
  };

  const columns = useMemo(
    () => [
      columnHelper.accessor(row => row, {
        id: "dealer",
        size: 80,
        header: () => <span className="no-wrap-text"> {t("v8_dealer", "Dealer")} </span>,
        cell: ({
          row: {
            original: { dealerName, locationId }
          }
        }) => {
          if (dealerName) return <span className="no-wrap-text">{dealerName}</span>;

          const dealerId = locations.find(loc => loc.id === locationId)?.dealer_id;
          const locationDealerName = dealers.find(d => d.id === dealerId)?.name;
          return <span className="no-wrap-text">{locationDealerName}</span>;
        }
      }),
      columnHelper.accessor(row => row.locationName, {
        id: "location",
        size: 80,
        header: () => <span className="no-wrap-text"> {t("v8_location", "Location")} </span>
      }),
      columnHelper.accessor(row => row, {
        id: "last_invoice_date",
        size: 200,
        header: () => <span className="no-wrap-text"> {t("v8_last_invoice_date", "Last Invoice Date")} </span>,
        cell: ({
          row: {
            original: { lastInvoiceDate }
          }
        }) => {
          if (!lastInvoiceDate)
            return (
              <span className="no-wrap-text">
                {t("v8_no_invoice_has_been_sent_yet_this_month_for_this_dealer_location", "No invoice has been sent yet this month for this dealer/location")}.
              </span>
            );

          return <span className="no-wrap-text">{formatDate(lastInvoiceDate, DATE_FORMATS_FNS.dateMonthYear)}</span>;
        }
      }),
      columnHelper.accessor(row => row, {
        id: "error",
        size: 200,
        header: () => <span className="no-wrap-text"> {t("v8_error", "Error")} </span>,
        cell: ({
          row: {
            original: { invoiceError }
          }
        }) => <CopyToClipboard text={invoiceError || ""} showPopup onSuccess={() => toast.success(t("v8_copy_to_clipboard", "Copied to clipboard"))} />
      }),
      columnHelper.accessor(row => row, {
        id: "retry_delete_failed_invoice",
        size: 200,
        header: () => "",
        cell: ({
          row: {
            original: { exactFailedInvoices, dealerId, locationId }
          }
        }) => {
          if (!exactFailedInvoices || exactFailedInvoices.length <= 0) return null;

          return (
            <div className="button-container">
              <Button
                size="small"
                color="orange"
                onClick={e => {
                  e.stopPropagation();
                  handleOpenFailedInvoiceModal({ ...(dealerId ? { dealerId: dealerId } : { dealerLocationId: locationId }) });
                }}
              >
                {t("v8_retry_delete_failed_invoice", "Retry/Delete failed invoice")}
              </Button>
            </div>
          );
        }
      })
    ],
    [t, locations, dealers, handleOpenFailedInvoiceModal]
  );

  const getExactFailedInvoices = (): FailedInvoiceModalPayload | null => {
    if (!selectedDealerOrLocationId) return null;

    if (selectedDealerOrLocationId.dealerId)
      return {
        dealerId: selectedDealerOrLocationId.dealerId,
        exactFailedInvoices: invoicedDealersAndLocations.find(({ dealerId }) => dealerId === selectedDealerOrLocationId.dealerId)?.exactFailedInvoices || []
      };

    if (selectedDealerOrLocationId.dealerLocationId)
      return {
        dealerLocationId: selectedDealerOrLocationId.dealerLocationId,
        exactFailedInvoices: invoicedDealersAndLocations.find(({ locationId }) => locationId === selectedDealerOrLocationId.dealerLocationId)?.exactFailedInvoices || []
      };

    return null;
  };

  const failedInvoiceModalPayload = getExactFailedInvoices();

  return (
    <>
      <div className="BillingTable">
        <ReactTable
          isFetching={isLoading}
          columns={columns}
          data={invoicedDealersAndLocations}
          onRowClick={({ dealerId, locationId }: DealersAndLocationsWithFailedInvoices) => handleOpenShowInvoiceHistoryModal(dealerId, locationId)}
        />
      </div>
      <FailedInvoicesModal
        isOpen={Boolean(failedInvoiceModalPayload?.exactFailedInvoices.length)}
        onClose={handleCloseFailedInvoiceModal}
        exactFailedInvoices={failedInvoiceModalPayload?.exactFailedInvoices || []}
        dealerId={failedInvoiceModalPayload?.dealerId}
        dealerLocationId={failedInvoiceModalPayload?.dealerLocationId}
      />
      <InvoiceHistoryModal dealerId={selectedDealerIdForInvoices} dealerLocationId={selectedLocationIdForInvoices} onClose={handleCloseInvoiceHistoryModal} />
    </>
  );
};
